<template>
	<div class="content-wrapper">
		<!-- Content -->

		<div class="container flex-grow-1 container-p-y">

			<div class="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-6 row-gap-4"  v-if="conventions.length > 0 && billingMethod !== 'invoices'">
				<div class="d-flex flex-column justify-content-center">
					<h4 class="mb-1">{{ $t('conventions.title') }}</h4>
					<p class="mb-0">{{ $t('conventions.description') }}</p>
				</div>
			</div>

			<div class="spinner-border spinner-border-lg text-primary load-center" role="status" v-if="isLoading">
				<span class="visually-hidden">{{ $t('common.loading') }}</span>
			</div>

			<div class="col-lg-12" v-if="!isLoading && conventions.length > 0 && billingMethod !== 'invoices'">
				<div class="card h-100">
					<div class="table-responsive">
						<table class="table table-borderless">
							<thead>
								<tr class="border-bottom">
									<th class="bg-transparent fs-xsmall">{{ $t('conventions.number') }}</th>
									<th class="bg-transparent fs-xsmall">{{ $t('conventions.amount') }}</th>
									<th class="bg-transparent fs-xsmall">{{ $t('common.fields.status') }}</th>
									<th class="bg-transparent fs-xsmall">{{ $t('conventions.issuedAt') }}</th>
									<th class="bg-transparent fs-xsmall">{{ $t('common.fields.actions') }}</th>
								</tr>
							</thead>
							<tbody>
								<tr v-for="(convention, index) in conventions" :key="index">
									<td>
										<div class="d-flex justify-content-start align-items-center">
											<div v-if="convention.users.length > 0">
												<h6 class="mb-0 text-truncate">
													{{ $t('conventions.conventionTitle', { reference: convention.orderNumber ? convention.orderNumber : convention.slug }) }}
												</h6>
												<span v-for="(user, index) in convention.users" :key="user._id">
													{{ user.firstName }} {{ user.lastName }}<span v-if="index < convention.users.length - 1">, </span>
												</span>
											</div>
										</div>
									</td>
									<td>
										{{ convention.billingMethod === 'invoices' ? convention.priceInCredits + ' €' : convention.priceInCredits + ' crédits' }}
									</td>
									<td>
										<span class="badge bg-label-success rounded-pill">{{ $t('conventions.available') }}</span>
									</td>
									<td>
										{{ formatDate(convention.createdAt) }}
									</td>
									<td>
										<a href="javascript:void(0);" class="btn btn-sm btn-icon btn-text-secondary rounded-pill me-1 waves-effect waves-light" @click="downloadConvention(convention._id)" >
											<i class="ri-download-2-line ri-22px"></i>
										</a>
									</td>
								</tr>
							</tbody>
						</table>
					</div>
				</div>
			</div>

			<div class="card" v-if="!isLoading && billingMethod === 'invoices'">
				<div class="card-body row gy-4">
					<div class="col-sm-12 col-lg-12 text-center px-4">
						<div class="d-flex justify-content-center">
							<div class="avatar avatar-lg mb-4">
								<span class="avatar-initial rounded-3 bg-label-primary">
									<i class="ri-folders-line ri-36px"></i>
								</span>
							</div>
						</div>
						<h4 class="card-title mb-4">{{ $t('conventions.monthlyBillingTitle') }}</h4>
						<p class="card-text">
							{{ $t('conventions.monthlyBillingDescription') }}
						</p>
						<router-link to="/opco">
							<button class="btn btn-primary mt-5">{{ $t('conventions.viewOpco') }}</button>
						</router-link>
						<br />
					</div>
				</div>
			</div>

			<div class="card" v-if="!isLoading && conventions.length === 0 && billingMethod !== 'invoices'">
				<div class="card-body row gy-4">
					<div class="col-sm-12 col-lg-12 text-center px-4">
						<div class="d-flex justify-content-center">
							<div class="avatar avatar-lg mb-4">
								<span class="avatar-initial rounded-3 bg-label-primary">
									<i class="ri-file-marked-line ri-36px"></i>
								</span>
							</div>
						</div>
						<h4 class="card-title mb-4">{{ $t('conventions.noConventions') }}</h4>
						<p class="card-text">
							{{ $t('conventions.noConventionsDescription') }}
						</p>
						<router-link to="/trainings/create">
							<button class="btn btn-primary mt-5">{{ $t('conventions.orderFirstTraining') }}</button>
						</router-link>
					</div>
				</div>
			</div>
		</div>

		<!-- / Content -->

		<div class="content-backdrop fade"></div>
	</div>
</template>

<script>
import HTTP from "@/axios";
import moment from "moment";
import "moment/locale/fr";

moment.locale(localStorage.getItem('locale') || 'fr');

export default {
	name: "ConventionPages",

	data() {
		return {
			isLoading: true,
			displayConventions: false,
			conventions: [],
		};
	},

	methods: {
		async fetchConventions() {
			try {
				const response = await HTTP('/conventions');
				this.conventions = response.data.conventions;
				this.billingMethod = response.data.billingMethod;
				this.isLoading = false;

				if (this.displayConventions)
					this.billingMethod = "credits";

			} catch (error) {
				// Récupérer le message d'erreur de l'API ou définir un message par défaut
				const errorMessage = error?.response?.data?.message || this.$t('errors.generic');

				alert(errorMessage);

				// Log détaillé dans la console (utile en développement)
				console.error("Erreur API:", error);
			}
		},

		async downloadConvention(conventionId) {
			try {
				// Appel API pour récupérer le PDF en base64
				const response = await HTTP.post("/conventions/download-convention", { conventionId });

				if (response.data?.base64) {
					// Convertir la base64 en un Blob et créer un lien de téléchargement
					const byteCharacters = atob(response.data.base64);
					const byteNumbers = new Array(byteCharacters.length);
					for (let i = 0; i < byteCharacters.length; i++) {
						byteNumbers[i] = byteCharacters.charCodeAt(i);
					}
					const byteArray = new Uint8Array(byteNumbers);
					const blob = new Blob([byteArray], { type: 'application/pdf' });

					// Créer un lien pour le téléchargement
					const link = document.createElement('a');
					link.href = URL.createObjectURL(blob);
					link.download = `convention_${conventionId}.pdf`;
					document.body.appendChild(link);
					link.click();
					document.body.removeChild(link);
				} else {
					throw new Error("Fichier non disponible.");
				}
			} catch (error) {
				// Gestion des erreurs
				const errorMessage = error?.response?.data?.message || 
					this.$t('errors.generic');
				alert(errorMessage);
				console.error("Erreur API:", error);
			}
		},

		formatDate(date) {
			return moment(date).format("DD/MM/YYYY"); // Affiche "il y a 3 semaines"
		},

		formatRelativeDate(date) {
			return moment(date).fromNow(); // Affiche "il y a 3 semaines"
		},
	},

	async mounted() {
		this.fetchConventions();
		this.displayConventions = this.$route.query.display;
	}
};
</script>

<style scoped>
/* Ajoutez des styles spécifiques à la page Dashboard */
.dashboard {
	padding: 1rem;
}

.card-title {
	font-size: 1.2rem;
}

.card-text {
	margin: 0;
}

.load-center {
	display: block;
	margin: 0 auto;
}
</style>
